import {
    ArrowDown,
    Box,
    Caption,
    Chip,
    DropdownContent,
    DropdownMenu,
    DropdownTrigger,
    TextBlock,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

import workflowStyle from "./all-workflows-table.scss";
import Chevron from "components/icons/Chevron";
import { ConnectedWorkflows } from "domain/workflows";
import { featureFlagService, FLAG_UICOMPS_WORKFLOWS_TABLE } from "services/feature/FeatureFlagService";
import { toUniqueArray } from "utils/commonFunctions";
import { findScrollableAncestor, useCloseDropdownOnScroll } from "utils/useCloseDropdownOnScroll";
import { useOutsideClick } from "utils/useOutsideClick";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

const FIELD_PARENT_WORKFLOWS = "parentWorkflows";
const FIELD_SUB_WORKFLOWS = "subWorkflows";
const FIELD_RUN_DIAGNOSE_WORKFLOWS = "runDiagnoseWorkflows";
const FIELD_RUN_PROCESSFLOW_WORKFLOWS = "runProcessflowWorkflows";
const FIELD_COLLECT_WORKFLOWS = "collectWorkflows";
const FIELD_GRADING_PANEL_WORKFLOWS = "gradingPanelWorkflows";
const FIELD_FOR_EACH_WORKFLOWS = "forEachWorkflows";
const FIELD_ADVANCED_GRADING_WORKFLOWS = "advancedGradingWorkflows";

interface Props {
    connectedWorkflows: ConnectedWorkflows;
    isSubWorkflow: boolean;
}

function buildTitles(isSubWorkflow: boolean, t: (key: string) => string): Record<string, string> {
    return {
        [FIELD_PARENT_WORKFLOWS]: isSubWorkflow
            ? t("WorkflowsTable.connectedWorkflows.template")
            : t("WorkflowsTable.connectedWorkflows.parentWorkflows"),
        [FIELD_SUB_WORKFLOWS]: t("WorkflowsTable.connectedWorkflows.subWorkflows"),
        [FIELD_RUN_DIAGNOSE_WORKFLOWS]: t("WorkflowsTable.connectedWorkflows.runDiagnoseWorkflows"),
        [FIELD_RUN_PROCESSFLOW_WORKFLOWS]: t("WorkflowsTable.connectedWorkflows.runProcessflowWorkflows"),
        [FIELD_COLLECT_WORKFLOWS]: t("WorkflowsTable.connectedWorkflows.collectWorkflows"),
        [FIELD_GRADING_PANEL_WORKFLOWS]: t("WorkflowsTable.connectedWorkflows.gradingPanelWorkflows"),
        [FIELD_FOR_EACH_WORKFLOWS]: t("WorkflowsTable.connectedWorkflows.forEachWorkflows"),
        [FIELD_ADVANCED_GRADING_WORKFLOWS]: t("WorkflowsTable.connectedWorkflows.advancedGradingWorkflows"),
    };
}

export default function ConnectedWorkflowsList(props: Props) {
    const { t } = useTranslation();
    const buttonRef = React.useRef(null);
    const [isDropdownOpen, setDropdownOpen] = React.useState(false);
    const theme = useTheme();
    const ref = React.useRef<HTMLDivElement>(null);
    useCloseDropdownOnScroll(isDropdownOpen, findScrollableAncestor(ref.current));

    // TODO BCC-8002: Remove this and all redundant code and styling after Workflows table rewrite is complete
    const isWorkflowsTableFeatureEnabled = featureFlagService.isFeatureEnabled(FLAG_UICOMPS_WORKFLOWS_TABLE);

    const createDropdownArrow = (isExpanded: boolean) => {
        return (
            <div>
                <Chevron
                    color={theme.iconFillColor}
                    className={classNames(workflowStyle.toggleArrow, workflowStyle.down, {
                        [workflowStyle.up]: isExpanded,
                    })}
                    width={20}
                    height={20}
                />
            </div>
        );
    };
    const handleClickOutside = () => {
        setDropdownOpen(false);
    };

    const connectedWorkflowsRef = useOutsideClick(handleClickOutside);

    const createConnectedWorkflowsList = (): JSX.Element => {
        const elements: JSX.Element[] = [];
        const titles = buildTitles(props.isSubWorkflow, t);
        const connectedWorkflowActivityList = (activity: string, workflows: string[]) => {
            const deduplicated = toUniqueArray(workflows);

            if (deduplicated.length > 0) {
                elements.push(<div className={workflowStyle.title}>{titles[activity]}:</div>);
                deduplicated.map((each) => {
                    elements.push(<div className={workflowStyle.workflowName}>{each}</div>);
                });
            }
        };

        connectedWorkflowActivityList(FIELD_PARENT_WORKFLOWS, props.connectedWorkflows.parentWorkflows);
        connectedWorkflowActivityList(FIELD_SUB_WORKFLOWS, props.connectedWorkflows.subWorkflows);
        connectedWorkflowActivityList(FIELD_RUN_DIAGNOSE_WORKFLOWS, props.connectedWorkflows.runDiagnoseWorkflows);
        connectedWorkflowActivityList(
            FIELD_RUN_PROCESSFLOW_WORKFLOWS,
            props.connectedWorkflows.runProcessFlowWorkflows
        );
        connectedWorkflowActivityList(FIELD_GRADING_PANEL_WORKFLOWS, props.connectedWorkflows.gradingPanelWorkflows);
        connectedWorkflowActivityList(FIELD_COLLECT_WORKFLOWS, props.connectedWorkflows.collectWorkflows);
        connectedWorkflowActivityList(FIELD_FOR_EACH_WORKFLOWS, props.connectedWorkflows.forEachWorkflows);
        connectedWorkflowActivityList(
            FIELD_ADVANCED_GRADING_WORKFLOWS,
            props.connectedWorkflows.advancedGradingWorkflows
        );

        if (props.connectedWorkflows.remoteWorkflows && props.connectedWorkflows.remoteWorkflows) {
            elements.push(
                <div className={workflowStyle.title}>
                    {t("WorkflowsTable.connectedWorkflows.remoteWorkflows")}: {props.connectedWorkflows.remoteWorkflows}
                </div>
            );
        }

        return (
            <div>
                {elements.map((each, index) => {
                    return <div key={index}>{each}</div>;
                })}
            </div>
        );
    };

    const createItems = () => {
        const titles = buildTitles(props.isSubWorkflow, t);

        const sections = [
            { field: FIELD_PARENT_WORKFLOWS, workflows: props.connectedWorkflows.parentWorkflows },
            { field: FIELD_SUB_WORKFLOWS, workflows: props.connectedWorkflows.subWorkflows },
            { field: FIELD_RUN_DIAGNOSE_WORKFLOWS, workflows: props.connectedWorkflows.runDiagnoseWorkflows },
            { field: FIELD_RUN_PROCESSFLOW_WORKFLOWS, workflows: props.connectedWorkflows.runProcessFlowWorkflows },
            { field: FIELD_GRADING_PANEL_WORKFLOWS, workflows: props.connectedWorkflows.gradingPanelWorkflows },
            { field: FIELD_COLLECT_WORKFLOWS, workflows: props.connectedWorkflows.collectWorkflows },
            { field: FIELD_FOR_EACH_WORKFLOWS, workflows: props.connectedWorkflows.forEachWorkflows },
            { field: FIELD_ADVANCED_GRADING_WORKFLOWS, workflows: props.connectedWorkflows.advancedGradingWorkflows },
        ];

        return (
            <>
                {sections.map((section) => {
                    const workflowNames = toUniqueArray(section.workflows);
                    if (workflowNames.length === 0) {
                        return null;
                    }
                    return (
                        <Box key={section.field} columnLayout>
                            <Caption greyTextStyle>{`${titles[section.field]}:`}</Caption>
                            {workflowNames.map((name) => (
                                <TextBlock key={name} disableBottomSpacing>
                                    {name}
                                </TextBlock>
                            ))}
                        </Box>
                    );
                })}

                {props.connectedWorkflows.remoteWorkflows > 0 && (
                    <Caption greyTextStyle>
                        {`${t("WorkflowsTable.connectedWorkflows.remoteWorkflows")}: `}
                        {props.connectedWorkflows.remoteWorkflows}
                    </Caption>
                )}
            </>
        );
    };

    if (!isWorkflowsTableFeatureEnabled) {
        return (
            <div
                className={workflowStyle.connectedWorkflowColumn}
                ref={connectedWorkflowsRef}
                data-testid={testIds.workArea.workflows.table.connectedWorkflows.itself}
            >
                <div
                    className={workflowStyle.connectedWorkflows}
                    onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen((prev) => !prev);
                    }}
                >
                    <div className={workflowStyle.connectedWorkflowsCount}>
                        {props.connectedWorkflows.totalConnectedWorkflows}
                    </div>

                    <button
                        ref={buttonRef}
                        data-testid={testIds.workArea.workflows.table.connectedWorkflows.button}
                        aria-expanded={isDropdownOpen}
                    >
                        {createDropdownArrow(isDropdownOpen)}
                    </button>
                </div>
                {isDropdownOpen && (
                    <div
                        data-testid={testIds.workArea.workflows.table.connectedWorkflows.details}
                        className={workflowStyle.listDetails}
                    >
                        {createConnectedWorkflowsList()}
                    </div>
                )}
            </div>
        );
    }

    return (
        <Box onClick={(event) => event.stopPropagation()} ref={ref}>
            <DropdownMenu
                closeOnSelection
                dropdownOffset="Xxs"
                onDropdownOpenChange={(isOpen: boolean) => setDropdownOpen(isOpen)}
                position="bottom-start"
                flipOptions={{
                    boundary: findScrollableAncestor(ref.current),
                    fallbackPlacements: ["bottom-end", "top-start", "top-end"],
                }}
            >
                <DropdownTrigger>
                    <Chip>
                        <Box verticalAlignment="Center" gap="Xxxs">
                            <TextBlock disableBottomSpacing>
                                {props.connectedWorkflows.totalConnectedWorkflows}
                            </TextBlock>
                            <ArrowDown
                                className={classNames(workflowStyle.toggleArrow, workflowStyle.down, {
                                    [workflowStyle.up]: isDropdownOpen,
                                })}
                            />
                        </Box>
                    </Chip>
                </DropdownTrigger>
                <DropdownContent>
                    <Box columnLayout padding="Xxs" gap="Xxs">
                        {createItems()}
                    </Box>
                </DropdownContent>
            </DropdownMenu>
        </Box>
    );
}
